import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { ChevronRight, Loader2 } from "lucide-react";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "../../ui/table";
import StatusBadge from "./StatusBadge";
import SkeletonCard from "./SkeletonCard";
import { User } from "lucide-react";

const RecentlyEditedReports = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch reports from backend
  useEffect(() => {
    const fetchReports = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/reports`
        );
        const reportsData = response.data.reports;

        const enrichedReports = await Promise.all(
          reportsData.map(async (report) => {
            let songTitle =
              report.songTitle || t("recentlyEditedReports.noReports");
            if (!report.songTitle) {
              try {
                const songResponse = await axios.get(
                  `${process.env.REACT_APP_API_BASE_URL}/api/ArbSongs/getArbSongBySongID/${report.songID}`
                );
                songTitle =
                  songResponse.data.title ||
                  t("recentlyEditedReports.noReports");
              } catch (songError) {
                console.error(
                  `Error fetching song title for songID ${report.songID}:`,
                  songError
                );
              }
            }
            return { ...report, songTitle };
          })
        );

        setReports(enrichedReports);
      } catch (err) {
        console.error("Error fetching reports:", err);
        setError(t("recentlyEditedReports.error"));
      } finally {
        setLoading(false);
      }
    };

    fetchReports();
  }, [t]);

  // In RecentlyEditedReports.js
  const handleEditReport = (report) => {
    const { _id, songID, partType, verseIndex, lineIndex, language } = report;
    const queryParams = new URLSearchParams({
      partType,
      lineIndex: lineIndex.toString(),
      reportId: _id,
      language, // Add language parameter
      ...(partType === "verse" && { verseIndex: verseIndex.toString() }),
    }).toString();
    navigate(`/edit-song/${songID}?${queryParams}`);
  };

  // Format date
  const formatDate = (isoDate) => {
    return new Date(isoDate).toLocaleDateString(i18n.language, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Capitalize language and status
  const formatLanguage = (language) => {
    return language.charAt(0).toUpperCase() + language.slice(1);
  };

  const formatStatus = (status) => {
    return t(`recentlyEditedReports.status.${status.toLowerCase()}`);
  };

  // Get status color class
  const getStatusColorClass = (status) => {
    switch (status.toLowerCase()) {
      case "pending":
        return "text-yellow-600 bg-yellow-100";
      case "resolved":
        return "text-green-600 bg-green-100";
      case "dismissed":
        return "text-red-600 bg-red-100";
      default:
        return "text-stone-300 bg-gray-100";
    }
  };

  const isRTL = i18n.language === "ar";

  const showAll = reports.length > 6;
  const displayedReports = showAll ? reports.slice(0, 6) : reports;

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {[...Array(3)].map((_, i) => (
          <SkeletonCard key={i} />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Card className="rounded-2xl border border-gray-100 shadow">
        <CardContent className="p-4 text-red-500 text-center">
          {error}
        </CardContent>
      </Card>
    );
  }

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-xl font-semibold text-neutral-700 flex items-center gap-2">
          {t("recentlyEditedReports.title")}
        </h2>
        {showAll && (
          <button
            className="px-4 py-1 bg-black text-white rounded-lg text-sm font-medium hover:bg-gray-900 transition-colors shadow"
            onClick={() => navigate("/all-reports")}
          >
            {t("recentlyEditedReports.showAll", "Show All")}
          </button>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {displayedReports.length === 0 ? (
          <Card className="rounded-2xl border border-gray-100 shadow">
            <CardContent className="py-8 text-center text-neutral-700 text-sm">
              {t("recentlyEditedReports.noReports")}
            </CardContent>
          </Card>
        ) : (
          displayedReports.map((report) => (
            <Card
              key={report._id}
              className="rounded-2xl border border-gray-100 shadow hover:shadow-lg transition-shadow group cursor-pointer"
              onClick={() => handleEditReport(report)}
            >
              <CardContent className="p-4 flex flex-col gap-2">
                <div className="flex items-center gap-3 mb-1">
                  <div className="bg-zinc-100 rounded-full p-2">
                    <User className="w-6 h-6 text-zinc-400" />
                  </div>
                  <div>
                    <div className="font-medium text-neutral-800 text-base truncate">
                      {report.songTitle}
                    </div>
                    <div className="text-xs text-zinc-400">
                      {formatLanguage(report.language)}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <StatusBadge status={report.status}>
                    {formatStatus(report.status)}
                  </StatusBadge>
                  <span className="text-xs text-zinc-400 ml-2">
                    {formatDate(report.createdAt)}
                  </span>
                </div>
                <button
                  className="mt-3 self-end px-4 py-1 bg-zinc-800 text-white rounded-lg text-sm font-medium hover:bg-black transition-colors shadow"
                  onClick={e => { e.stopPropagation(); handleEditReport(report); }}
                >
                  {t("recentlyEditedReports.editButton")}
                </button>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default RecentlyEditedReports;
